import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelTextGen } from '../layoutLabelText';
import { OptsLabelText } from '../optsLabelText';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';

/**
 * Kolicky_Pismena (Letter Selection Wheels)
 *
 * Layout: 3 landscape pages with increasing option counts (2, 3, 4).
 * Each page: 5×2 grid. Each cell has an image on top and
 * multiple-choice letter options at the bottom.
 *
 * Migrated from C# EduFrame.Img10.Generators.Kolicky__Pismena
 */
export class Kolicky__Pismena implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const { origin, size } = calcOrigin(5, 2, margin, PdfSize.A4H);
    const {random} = opts.data;

    const allLabels = new Set<string>();
    for (const item of opts.list) {
      allLabels.add(item.text.substring(0, 1).toUpperCase());
    }
    const labels = Array.from(allLabels);

    for (let count = 2; count <= 4; count++) {
      const totalPages = Math.ceil(opts.list.length / 10);

      for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
        const page = pdf.addPage(true);
        const startItem = pageIdx * 10;
        const endItem = Math.min(startItem + 10, opts.list.length);

        const optionWidth = size.width / count;
        const optionHeight = PdfUnit.FromMillimeters(15);
        const labelFont = page.adjustFont(optionHeight, labels, GUtil.baseFont);

        for (let x = 0; x < 5; x++) {
          for (let y = 0; y < 2; y++) {
            const ind = y * 5 + x;
            const itemInd = startItem + ind;

            const rect = new PdfRect(
              origin.x + x * size.width,
              origin.y + y * size.height,
              size.width,
              size.height
            );
            const r1 = new PdfRect(rect.x, rect.y, rect.width, rect.height - optionHeight);

            if (itemInd < endItem) {
              page.drawLabel(opts.list[itemInd].label, { rect: r1 });
              page.drawRectangle(GUtil.basePen, null, rect);

              const correctLabel = opts.list[itemInd].text.substring(0, 1).toUpperCase();
              const wrongLabels = labels
                .filter((l) => l !== correctLabel)
                .sort(() => random.next(0, 2) - 1)
                .slice(0, count - 1);
              const options = [...wrongLabels, correctLabel].sort(() => random.next(0, 2) - 1);

              for (let i = 0; i < options.length; i++) {
                const r = new PdfRect(
                  rect.x + i * optionWidth,
                  rect.bottom - optionHeight,
                  optionWidth,
                  optionHeight
                );
                page.drawString(options[i], labelFont, PdfBrush.Black, null, r, ETextAlign.Center);
                page.drawRectangle(GUtil.basePen, null, r);
              }
            } else {
              page.drawRectangle(GUtil.basePen, null, rect);
              for (let i = 0; i < count; i++) {
                const r = new PdfRect(
                  rect.x + i * optionWidth,
                  rect.bottom - optionHeight,
                  optionWidth,
                  optionHeight
                );
                page.drawRectangle(GUtil.basePen, null, r);
              }
            }
          }
        }
      }
    }
  }
}
